import { z } from "zod";
import { SmartCreationDraftSchema, SmartCreationStructureSchema } from "./smart-creation/draft";
import { validateSmartCreationStrategySeedRule } from "./smart-creation/strategy-configuration";
import {
  SmartCreationStrategyBidSchema,
  SmartCreationStrategyEntityIdSchema,
  SmartCreationStrategyLaneSchema,
  SmartCreationStrategyTargetIntentSchema,
  SmartCreationTargetingStrategyKindSchema,
  smartCreationStrategyLaneKind,
} from "./smart-creation/strategy-targets";

export * from "./smart-creation/draft";
export * from "./smart-creation/draft-content";
export * from "./smart-creation/objectives";
export {
  createDefaultSmartCreationTargetingStrategyConfiguration,
  effectiveSmartCreationStrategyLanes,
  isSmartCreationStrategyTargetActive,
  pendingSmartCreationStrategyLanes,
  type SmartCreationStrategySeedProvenance,
  SmartCreationStrategySeedProvenanceSchema,
  type SmartCreationStrategySeedRule,
  SmartCreationStrategySeedRuleSchema,
  type SmartCreationTargetingStrategyBranch,
  SmartCreationTargetingStrategyBranchSchema,
  type SmartCreationTargetingStrategyConfiguration,
  SmartCreationTargetingStrategyConfigurationSchema,
  smartCreationStrategyTargetLane,
  smartCreationStrategyTargetScopesOverlap,
} from "./smart-creation/strategy-configuration";
export * from "./smart-creation/strategy-recommendation";
export {
  canonicalSmartCreationTargetIntentKey,
  type SmartCreationAutoTargetExpression,
  SmartCreationAutoTargetExpressionSchema,
  type SmartCreationStrategyLane,
  SmartCreationStrategyLaneSchema,
  type SmartCreationStrategyTarget,
  type SmartCreationStrategyTargetIntent,
  SmartCreationStrategyTargetIntentSchema,
  type SmartCreationStrategyTargetProvenance,
  SmartCreationStrategyTargetProvenanceSchema,
  SmartCreationStrategyTargetSchema,
  type SmartCreationStrategyTargetSource,
  SmartCreationStrategyTargetSourceSchema,
  type SmartCreationTargetingStrategyKind,
  SmartCreationTargetingStrategyKindSchema,
  smartCreationStrategyLaneKind,
} from "./smart-creation/strategy-targets";
export * from "./smart-creation/structure-evaluation";

export const SmartCreationDraftUpdateInputSchema = z
  .object({
    expectedRevision: z.number().int().positive(),
    draft: SmartCreationDraftSchema,
  })
  .strict();
export const SmartCreationDraftGeneratePreviewInputSchema = z
  .object({ expectedRevision: z.number().int().positive() })
  .strict();
export const SmartCreationStructureUpdateInputSchema = z
  .object({
    expectedRevision: z.number().int().positive(),
    structure: SmartCreationStructureSchema,
  })
  .strict();
export type SmartCreationStructureUpdateInput = z.infer<
  typeof SmartCreationStructureUpdateInputSchema
>;
export const SmartCreationStructureEvaluationInputSchema = z
  .object({ expectedRevision: z.number().int().positive() })
  .strict();
export type SmartCreationStructureEvaluationInput = z.infer<
  typeof SmartCreationStructureEvaluationInputSchema
>;
export const SmartCreationObjectiveRecommendationPreviewInputSchema = z
  .object({ draft: SmartCreationDraftSchema })
  .strict();
export const SmartCreationStrategyRecommendationInputSchema = z
  .object({ expectedRevision: z.number().int().positive() })
  .strict();
export type SmartCreationStrategyRecommendationInput = z.infer<
  typeof SmartCreationStrategyRecommendationInputSchema
>;
const SmartCreationStrategyKindWithoutAutomaticSchema = z.enum([
  "research",
  "performance",
  "branded",
  "competitor",
]);
export const SmartCreationStrategyRecommendationAcceptInputSchema = z
  .object({
    expectedRevision: z.number().int().positive(),
    recommendationId: z.string().uuid(),
    strategies: z
      .array(SmartCreationStrategyKindWithoutAutomaticSchema)
      .min(1)
      .max(4)
      .refine((strategies) => new Set(strategies).size === strategies.length, {
        message: "Recommendation Strategies must be unique",
      }),
  })
  .strict();
export type SmartCreationStrategyRecommendationAcceptInput = z.infer<
  typeof SmartCreationStrategyRecommendationAcceptInputSchema
>;

const SmartCreationStrategyTargetCreateSchema = z
  .object({
    intent: SmartCreationStrategyTargetIntentSchema,
    bid: SmartCreationStrategyBidSchema,
  })
  .strict();
const SmartCreationStrategyTargetPatchSchema = z
  .object({
    intent: SmartCreationStrategyTargetIntentSchema.optional(),
    bid: SmartCreationStrategyBidSchema.optional(),
  })
  .strict()
  .refine((patch) => patch.intent !== undefined || patch.bid !== undefined, {
    message: "Target patch requires an intent or bid change",
  });
const SmartCreationStrategySeedCreateSchema = z
  .object({
    dimension: z.enum(["keyword", "asin", "brand"]),
    operator: z.enum(["equals", "contains"]),
    value: z.string().trim().min(1).max(200),
  })
  .strict()
  .superRefine(validateSmartCreationStrategySeedRule);
const SmartCreationStrategySeedPatchSchema = z
  .object({
    dimension: z.enum(["keyword", "asin", "brand"]).optional(),
    operator: z.enum(["equals", "contains"]).optional(),
    value: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
  .refine(
    (patch) =>
      patch.dimension !== undefined || patch.operator !== undefined || patch.value !== undefined,
    { message: "Seed patch requires a dimension, operator or value change" },
  );

export const SmartCreationStrategyOperationSchema = z
  .discriminatedUnion("operation", [
    z
      .object({
        operation: z.literal("set_lane_enabled"),
        strategyKind: SmartCreationStrategyKindWithoutAutomaticSchema,
        lane: SmartCreationStrategyLaneSchema.exclude(["automatic"]),
        enabled: z.boolean(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_strategy_enabled"),
        strategyKind: SmartCreationStrategyKindWithoutAutomaticSchema,
        enabled: z.boolean(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("add_target"),
        strategyKind: SmartCreationStrategyKindWithoutAutomaticSchema,
        target: SmartCreationStrategyTargetCreateSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("update_target"),
        strategyKind: SmartCreationStrategyKindWithoutAutomaticSchema,
        targetId: SmartCreationStrategyEntityIdSchema,
        patch: SmartCreationStrategyTargetPatchSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_target_included"),
        strategyKind: SmartCreationTargetingStrategyKindSchema,
        targetId: SmartCreationStrategyEntityIdSchema,
        included: z.boolean(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("remove_target"),
        strategyKind: SmartCreationStrategyKindWithoutAutomaticSchema,
        targetId: SmartCreationStrategyEntityIdSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("add_seed"),
        strategyKind: z.enum(["branded", "competitor"]),
        seed: SmartCreationStrategySeedCreateSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("update_seed"),
        strategyKind: z.enum(["branded", "competitor"]),
        seedId: SmartCreationStrategyEntityIdSchema,
        patch: SmartCreationStrategySeedPatchSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("remove_seed"),
        strategyKind: z.enum(["branded", "competitor"]),
        seedId: SmartCreationStrategyEntityIdSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("restore_source"),
        strategyKind: SmartCreationStrategyKindWithoutAutomaticSchema,
        source: z.enum(["entered", "recommended"]),
      })
      .strict(),
  ])
  .refine(
    (operation) =>
      operation.operation !== "set_lane_enabled" ||
      smartCreationStrategyLaneKind(operation.lane) === operation.strategyKind,
    { message: "Lane must belong to the selected Strategy", path: ["lane"] },
  );
export type SmartCreationStrategyOperation = z.infer<typeof SmartCreationStrategyOperationSchema>;

export const SmartCreationStrategyUpdateInputSchema = z
  .object({
    expectedRevision: z.number().int().positive(),
    operations: z.array(SmartCreationStrategyOperationSchema).min(1).max(100),
  })
  .strict();
export type SmartCreationStrategyUpdateInput = z.infer<
  typeof SmartCreationStrategyUpdateInputSchema
>;
